import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import * as _ from "lodash";
import { FileValidator, FileInputComponent, FileInput } from "ngx-material-file-input";
import { first } from "rxjs/operators";
import { Subscription } from "rxjs";

import { AppGlobals } from "../../../app.globals";
import { AdmissionsService, AlertService, MediaWatcherService } from "../../../_services";
import { UnSavedChangesDialogComponent } from "../../../_components/unSavedChangesDialog/unSavedChangesDialog.component";

@Component({
    templateUrl: "admissionsApplicationDocumentUploadDialog.component.html",
    styleUrls: ["admissionsApplicationDocumentUploadDialog.component.css"]
})
export class AdmissionsApplicationDocumentUploadDialogComponent implements OnInit, OnDestroy {
    documentFormGroup: UntypedFormGroup;
    admissionsApplicationId: number;
    id: number;
    rowversion: string;
    attachmentRowversion: string;
    attachmentId?: number;
    studentDocumentTypeId: number;
    studentDocumentType: string;
    fileToUpload: File = null;
    allowedUploadDocumentTypes: string;
    isMobile = false;
    mediaWatcherSubscription: Subscription;
    fileName: string;
    description: string;
    allowMultipleOfType = false;

    readonly maxSize = 2097152; // 2 MB

    @ViewChild("fileAttachment", { static: true }) fileAttachmentInput: FileInputComponent;

    constructor(private dialogRef: MatDialogRef<AdmissionsApplicationDocumentUploadDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: any,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private appGlobals: AppGlobals,
        private alertService: AlertService,
        private admissionsService: AdmissionsService,
        private mediaWatcherService: MediaWatcherService) {
        this.admissionsApplicationId = data.applicationId;
        this.studentDocumentTypeId = data.studentDocumentTypeId;
        this.studentDocumentType = data.studentDocumentType;
        this.id = data.id;
        this.rowversion = data.rowversion;
        this.attachmentRowversion = data.attachmentRowversion;
        this.attachmentId = data.attachmentId;
        this.allowedUploadDocumentTypes = data.allowedUploadDocumentTypes;
        this.fileName = data.fileName;
        this.description = data.description;
        this.allowMultipleOfType = data.allowMultipleOfType;
    }

    ngOnDestroy() {
        this.mediaWatcherSubscription.unsubscribe();
    }

    ngOnInit() {
        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;
        });

        this.initializeForm();
        this.onChanges();

        if (this.fileName) {
            const file = new FileInput([
                new File(["test"], this.fileName)
            ]);

            setTimeout(() => {
                this.fileAttachmentInput.value = file;
                this.documentFormGroup.get("fileAttachment").setValidators([FileValidator.maxContentSize(this.maxSize)]);
                this.documentFormGroup.get("fileAttachment").updateValueAndValidity();
            }, null);


            //this.documentFormGroup.get("fileAttachment").disable();
            //this.documentFormGroup.get("fileAttachment").setValidators(null);
        }

        this.documentFormGroup.get("description").setValue(this.description);
    }

    initializeForm() {
        this.documentFormGroup = this.formBuilder.group({
            fileAttachment: [{ value: null, disabled: false }, [Validators.required, FileValidator.maxContentSize(this.maxSize)]],
            description: [{ value: "", disabled: !this.allowMultipleOfType }, [Validators.required]]
        });
    }

    onChanges() {
        this.documentFormGroup.get("fileAttachment").valueChanges
            .subscribe(file => {
                if (!file)
                    this.documentFormGroup.get("fileAttachment").setValidators([Validators.required, FileValidator.maxContentSize(this.maxSize)]);
            });
    }

    get documentDetailsFormControls() { return this.documentFormGroup.controls as any; }

    saveDocumentDetails(submitBtn: HTMLButtonElement) {
        if (this.documentFormGroup.invalid || !this.customValidation()) {
            if (this.documentDetailsFormControls.fileAttachment.invalid) {
                this.documentDetailsFormControls.fileAttachment.markAsTouched({ onlySelf: true });
            }

            return;
        }

        //if there where any changes...update the backend
        if (this.documentFormGroup.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const documentDetailsModel = {
                id: this.id,
                admissionsApplicationId: this.admissionsApplicationId,
                fileName: this.documentDetailsFormControls.fileAttachment.value ? this.documentDetailsFormControls.fileAttachment.value.files[0].name : null,
                fileType: this.documentDetailsFormControls.fileAttachment.value ? this.documentDetailsFormControls.fileAttachment.value.files[0].type : null,
                attachmentId: this.attachmentId,
                description: !this.documentDetailsFormControls.description.value || !this.documentDetailsFormControls.description.value.trim() ? null : this.documentDetailsFormControls.description.value.trim(),
                studentDocumentTypeId: this.studentDocumentTypeId,
                rowversion: this.rowversion,
                attachmentRowversion: this.attachmentRowversion
            };

            const saveModel = new FormData();

            saveModel.append("documentDetailsModel", JSON.stringify(documentDetailsModel));

            if (this.documentDetailsFormControls.fileAttachment.value)
                saveModel.append("fileAttachment", this.documentDetailsFormControls.fileAttachment.value.files[0], this.documentDetailsFormControls.fileAttachment.value.files[0].name);

            this.admissionsService.insertOrUpdateAdmissionsApplicationDocumentDetails(saveModel)
                .pipe(first())
                .subscribe(() => {
                    this.alertService.showMessage("Document Successfully Submitted.", "Admissions Application", this.appGlobals.alertMessageTypes.success);

                    this.dialogRef.close(true);
                });
        }
    }

    customValidation() {
        if (this.documentDetailsFormControls.fileAttachment.value && _.find(this.documentDetailsFormControls.fileAttachment.value.files, (f: File) => {
            if (this.allowedUploadDocumentTypes === ".pdf, .doc, .docx")
                return f.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" && f.type !== "application/pdf";
            else
                return f.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" && f.type !== "application/pdf" && f.type !== "image/jpg" && f.type !== "image/jpeg" && f.type !== "image/png";
        })) {
            if (this.allowedUploadDocumentTypes === ".pdf, .doc, .docx")
                this.alertService.showMessage("Only pdf and word documents are allowed.", "Admissions Application", this.appGlobals.alertMessageTypes.error);
            else
                this.alertService.showMessage("Only pdf, word documents or images (jpg and png) are allowed.", "Admissions Application", this.appGlobals.alertMessageTypes.error);

            return false;
        }

        return true;
    }

    close(refresh) {
        if (this.documentFormGroup.dirty) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close(refresh);
                }
            });
        } else {
            this.dialogRef.close(refresh);
        }
    }

    handleFileInput(files: FileList) {
        this.fileToUpload = files.item(0);
    }
}